// Picks: the most recent entries across the app with their actual predictions,
// confidence, results and scores. Filter by league, then by round.
import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';
import { EmptyState, EntryCard, PageTitle, useAdminData } from './parts';

export default function Picks() {
  const { data } = useAdminData();
  const [leagueId, setLeagueId] = useState('all');
  const [roundId, setRoundId] = useState('all');

  const leagueOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of data.entries) m.set(e.leagueId, e.leagueName);
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [data.entries]);

  const roundOptions = useMemo(() => {
    const m = new Map<string, { label: string; latest: number }>();
    for (const e of data.entries) {
      if (leagueId !== 'all' && e.leagueId !== leagueId) continue;
      const cur = m.get(e.roundId);
      if (!cur || e.submittedAt > cur.latest) m.set(e.roundId, { label: e.roundLabel, latest: e.submittedAt });
    }
    return [...m.entries()]
      .map(([id, v]) => ({ id, label: v.label, latest: v.latest }))
      .sort((a, b) => b.latest - a.latest);
  }, [data.entries, leagueId]);

  const entries = useMemo(
    () =>
      data.entries.filter(
        (e) => (leagueId === 'all' || e.leagueId === leagueId) && (roundId === 'all' || e.roundId === roundId)
      ),
    [data.entries, leagueId, roundId]
  );

  function pickLeague(id: string) {
    setLeagueId(id);
    setRoundId('all');
  }

  return (
    <>
      <PageTitle
        title="Picks"
        sub={`${data.stats.totalPicks} total · showing ${entries.length}`}
      />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <FilterPill active={leagueId === 'all'} onClick={() => pickLeague('all')}>
          All leagues
        </FilterPill>
        {leagueOptions.map((l) => (
          <FilterPill key={l.id} active={leagueId === l.id} onClick={() => pickLeague(l.id)}>
            {l.name}
          </FilterPill>
        ))}

        {leagueId !== 'all' && roundOptions.length > 0 && (
          <div className="relative ml-auto">
            <select
              value={roundId}
              onChange={(e) => setRoundId(e.target.value)}
              className="num appearance-none rounded-full border border-black/10 bg-card py-2 pl-3.5 pr-9 text-[13px] font-semibold text-ink shadow-tile outline-none"
            >
              <option value="all">All rounds</option>
              {roundOptions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
            <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink3" />
          </div>
        )}
      </div>

      {entries.length === 0 ? (
        <EmptyState>No picks to show.</EmptyState>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {entries.map((e) => (
            <EntryCard key={`${e.userId}-${e.leagueId}-${e.roundId}`} entry={e} />
          ))}
        </div>
      )}
    </>
  );
}

function FilterPill({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'tap rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors',
        active ? 'bg-night text-white' : 'bg-fill text-ink2 hover:bg-fill2'
      )}
    >
      {children}
    </button>
  );
}
